/**
 * Session store — holds the currently authenticated user (or null when
 * unauthenticated).  `useMe` hydrates this after the /me round-trip;
 * the layout reads `useSession` to render the user menu and gate
 * role-scoped UI.  Shape mirrors `MeResponse` from the backend.
 */
import { create } from "zustand";
import type { MeResponse } from "@/api/auth";

export type SessionUser = MeResponse;
export type UserRole = SessionUser["role"];

interface SessionState {
  user: SessionUser | null;
  setUser: (user: SessionUser | null) => void;
  clear: () => void;
}

export const useSession = create<SessionState>((set) => ({
  user: null,
  setUser: (user) => set({ user }),
  clear: () => set({ user: null }),
}));

/**
 * Derived «this user leads at least one unit» flag. Authority to approve time,
 * manage own people, etc. comes from the org tree (`led_unit_ids`), not from a
 * role — see task 97. Pure so router `beforeLoad` guards can reuse it on the
 * raw `/me` payload; `useIsLead` is the reactive selector for components.
 */
export function isLeadUser(user: Pick<SessionUser, "led_unit_ids"> | null | undefined): boolean {
  return (user?.led_unit_ids?.length ?? 0) > 0;
}

export function useIsLead(): boolean {
  return useSession((s) => isLeadUser(s.user));
}

/**
 * Derived «this user acts on at least one unit» flag — the capability gate for
 * the approval console, backfill and people management (task 98 §2.2). Wider
 * than `isLeadUser`: it is true for a lead *and* for a plain deputy (whose
 * `led_unit_ids` is empty but `acting_unit_ids` is not). Admin is handled by a
 * `role === "admin"` branch and gets an empty `acting_unit_ids`, so this stays
 * false for them. Pure, so router `beforeLoad` guards can reuse it on the raw
 * `/me` payload; `useActsOnUnit` is the reactive selector for components.
 */
export function actsOnUnit(user: Pick<SessionUser, "acting_unit_ids"> | null | undefined): boolean {
  return (user?.acting_unit_ids?.length ?? 0) > 0;
}

export function useActsOnUnit(): boolean {
  return useSession((s) => actsOnUnit(s.user));
}

/**
 * Derived «this user participates in time tracking» flag (`tracks_time`, task
 * 110). The single source of self-entry surfaces: it gates the rail «Учёт
 * времени» item, the landing target and the `/field` route, mirroring the
 * backend subjecthood gate. Pure, so router `beforeLoad` guards can read it on
 * the raw `/me` payload; `useTracksTime` is the reactive selector.
 */
export function tracksTime(user: Pick<SessionUser, "tracks_time"> | null | undefined): boolean {
  return user?.tracks_time === true;
}

export function useTracksTime(): boolean {
  return useSession((s) => tracksTime(s.user));
}

export function isProjectManager(
  user: Pick<SessionUser, "is_project_manager"> | null | undefined,
): boolean {
  return user?.is_project_manager === true;
}

export function canEditProjects(
  user: Pick<SessionUser, "role" | "is_project_manager"> | null | undefined,
): boolean {
  return user?.role === "admin" || isProjectManager(user);
}

export function useCanEditProjects(): boolean {
  return useSession((s) => canEditProjects(s.user));
}
